import { getDebugState } from '@store/debugger';

import { useText } from './use-text';
import { DictionaryInterface, TranslateOptions } from './use-translate.types';

let dictionaryPromise: Promise<any> | undefined;

const showDictionaryKeys = getDebugState().showDictionaryKeys;

const normalizeKey = (keyRaw: string) => keyRaw.toLowerCase().replace(/[^a-zA-Z0-9A-zÀ-ú]/g, '');

const getTranslationByKey = (dictionary: DictionaryInterface, dictionaryKey: string) => {
    const translation = dictionary[dictionaryKey];

    let translatedValue = translation || '';

    if (showDictionaryKeys) {
        translatedValue = `${translatedValue} :: ${dictionaryKey}`;
    }

    return translatedValue;
};

const applyTranslateOptions = (translation: string, options?: TranslateOptions) => {
    if (options?.plurality) {
        options.plurality.forEach(({ id, value, total }) => {
            const plurality = translation.match(
                new RegExp(
                    `\\{id:\\s*${id}\\s*\\/one:([^\\/]*)\\/many:([^\\/\\}]*)(?:\\/none:([^\\/\\}]*))?(?:\\/max:([^\\}]*))?\\}`,
                ),
            );

            if (!plurality) return;

            let output: string;

            if (value <= 0) output = plurality[3] || plurality[2];
            else if (total && value >= total && plurality[4]) output = plurality[4];
            else if (value === 1) output = plurality[1];
            else output = plurality[2];

            translation = translation.replace(plurality[0], output.trim());
        });
    }

    if (options?.replace) {
        options.replace.forEach(({ id, value }) => {
            translation = translation.replace(new RegExp(`\\[${id}\\]`, 'g'), value);
        });
    }

    return translation;
};

const getDictionary = async (): Promise<any> => {
    if (dictionaryPromise) return await dictionaryPromise;

    dictionaryPromise = fetchDictionary();

    return await dictionaryPromise;
};

const fetchDictionary = async (): Promise<any> => {
    const { dictionaryUrl } = window.__ENVIRONMENT_DETAILS__;

    const response = await fetch(dictionaryUrl);
    const data = await response.json();

    return data;
};

export const translateItem = async (
    dictionaryKey: string,
    options?: TranslateOptions,
): Promise<string> => {
    const dictionaryStorage = sessionStorage.getItem('dictionary');
    const dictionaryFromStorage =
        dictionaryStorage && dictionaryStorage !== 'null' ? JSON.parse(dictionaryStorage) : [];

    if (dictionaryFromStorage && dictionaryFromStorage.length > 0) {
        return applyTranslateOptions(
            getTranslationByKey(dictionaryFromStorage, dictionaryKey),
            options,
        );
    }

    const dictionary = await getDictionary();

    if (dictionary) {
        try {
            sessionStorage.setItem('dictionary', JSON.stringify(dictionary));
        } catch (error) {
            console.log('Error in sessionStorage.setItem', error);
        }

        return applyTranslateOptions(getTranslationByKey(dictionary, dictionaryKey), options);
    }

    return showDictionaryKeys ? `:: ${dictionaryKey}` : '';
};

export const translateItems = async <T>(
    keys: { [K in keyof T]?: string },
    options?: TranslateOptions,
): Promise<{ [K in keyof T]?: string }> => {
    const translated: { [K in keyof T]?: string } = {};
    for (const key of Object.keys(keys) as (keyof T)[]) {
        if (!keys[key]) continue;
        translated[key] = await translateItem(keys[key]!, options);
    }
    return translated;
};

/**
 * @description Translates a single characteristic value. Format is as following: 'Feature.category.value'.
 * The `value` is based on the value in SF. Without white spaces and special characters. Numbers + accented characters are kept.
 * "Value example 1" will be: "valueExample1"
 * @param {string} prefix
 * @param {string} value
 * @return {*}
 */
export const normalizedTranslateItem = async (prefix: string, value: string) => {
    const key = normalizeKey(value);
    const translation = await translateItem(`${prefix}${key}`);
    return translation.length ? translation : value;
};

export const useTranslate = (item: string, options?: TranslateOptions) => {
    const [text, setText] = useText('');
    translateItem(item).then((result) => {
        if (!result) return;

        result = applyTranslateOptions(result, options);

        setText(result);

        if (options?.onUpdate) {
            options.onUpdate();
        }
    });
    return text;
};
